/**
 * Connect to Care — standalone page for the care-resources directory that
 * lives at the bottom of every scenario page (ScenarioCareResources).
 *
 * Lets a visitor pick their medical situation (scenario) and see the
 * financial-assistance / programs / peer-support / advocacy resources matched
 * to that scenario's tags, filtered to their state where resources are
 * state-specific. Reached from the "Connect to Care" (heart) entry in the
 * menu drawer.
 */
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Heart, Search, MapPin, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loadStaticData } from '@/lib/offlineData';
import { ScenarioCareResources } from '@/components/scenarios/ScenarioCareResources';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useBrand } from '@/config/brands';

interface ScenarioMeta {
  id: string;
  name: string;
  slug?: string | null;
  category?: string | null;
  tags?: string[];
}

export default function ConnectToCare() {
  const { t } = useTranslation('scenarios');
  const brand = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [zipInput, setZipInput] = useState('');
  const { location, isLocating, detectLocation, lookupZip } = useUserLocation(true);

  // Scenario catalog from the static index (CDN-cached, no DB roundtrip)
  const { data: scenarios = [], isLoading } = useQuery({
    queryKey: ['connect-to-care-scenarios'],
    queryFn: async () => {
      const idx = await loadStaticData<{ data: any[] }>('scenarios_index.json');
      return (idx?.data || []).map((s): ScenarioMeta => ({
        id: s.id || s.uuid,
        name: s.name,
        slug: s.slug,
        category: s.category,
        tags: Array.isArray(s.tags) ? s.tags : [],
      }));
    },
    staleTime: 60 * 60 * 1000,
  });

  // Selection persists in the URL (?scenario=<uuid>) so links are shareable
  const selectedId = searchParams.get('scenario');
  const selected = useMemo(
    () => scenarios.find(s => s.id === selectedId) || null,
    [scenarios, selectedId]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return scenarios
      .filter(s => s.name?.toLowerCase().includes(q))
      .slice(0, 30);
  }, [scenarios, query]);

  const select = (s: ScenarioMeta | null) => {
    setQuery('');
    if (s) setSearchParams({ scenario: s.id }, { replace: false });
    else setSearchParams({}, { replace: false });
  };

  useEffect(() => { window.scrollTo(0, 0); }, [selectedId]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Connect to Care — Financial Help & Support Programs | {brand.name}</title>
        <meta name="description" content="Find financial assistance, support programs, peer communities, and patient advocacy resources matched to your medical situation and state." />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8 pb-28">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Heart className="w-7 h-7 text-red-600" />
          <h1 className="text-2xl sm:text-3xl font-bold">{t('connectToCare.title')}</h1>
        </div>
        <p className="text-muted-foreground mb-6 max-w-2xl">
          {location?.state
            ? t('connectToCare.subtitleWithState', { state: location.state })
            : t('connectToCare.subtitle')}
        </p>

        {/* Location row */}
        <div className="flex flex-wrap items-center gap-2 mb-6 text-sm">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          {location?.state ? (
            <span className="text-muted-foreground">
              {t('connectToCare.showingStateResources')} <strong className="text-foreground">{location.state}</strong>
            </span>
          ) : (
            <span className="text-muted-foreground">{t('connectToCare.setLocationPrompt')}</span>
          )}
          <input
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => { if (e.key === 'Enter' && zipInput.length === 5) lookupZip(zipInput); }}
            placeholder={t('connectToCare.zipPlaceholder')}
            inputMode="numeric"
            className="w-24 px-2 py-1 rounded border border-border bg-card text-sm"
          />
          <button
            onClick={() => (zipInput.length === 5 ? lookupZip(zipInput) : detectLocation())}
            className="px-2.5 py-1 rounded border border-border text-sm hover:bg-muted transition-colors"
            disabled={isLocating}
          >
            {isLocating ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (zipInput.length === 5 ? t('connectToCare.set') : t('connectToCare.detect'))}
          </button>
        </div>

        {/* Scenario picker */}
        <div className="relative mb-8">
          {selected ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('connectToCare.yourSituation')}</div>
                <div className="font-semibold">{selected.name}</div>
              </div>
              <button
                onClick={() => select(null)}
                className="shrink-0 p-1.5 rounded hover:bg-muted transition-colors"
                title={t('connectToCare.changeSituation')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3">
                <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={isLoading ? t('connectToCare.loadingSituations') : t('connectToCare.searchPlaceholder')}
                  className="w-full bg-transparent outline-none text-sm"
                />
              </div>
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                  {results.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => select(s)}
                      className="block w-full text-left px-4 py-2.5 text-sm hover:bg-muted transition-colors border-b border-border/40 last:border-b-0"
                    >
                      {s.name}
                      {s.category ? <span className="ml-2 text-xs text-muted-foreground">{s.category}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Resources — scenario-matched when selected, general otherwise */}
        <ScenarioCareResources
          scenarioTags={selected?.tags || []}
          scenarioName={selected?.name || t('connectToCare.yourSituationFallback')}
          userState={location?.state}
        />
      </div>
    </div>
  );
}
